/**
 * RadarChartWithSelectorsView - Radar chart with metric selector overlays
 *
 * Displays the radar chart with positioned metric selectors around it.
 * The chart container uses `pointerEvents="none"` so it does not intercept
 * touches/clicks meant for the overlaid metric selector dropdowns.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import React from "react";

import PositionedMetricSelector from "@/features/player-detail/components/organisms/PositionedMetricSelector";

import { Box } from "@/components/atoms";
import RadarChart from "@/features/dashboard/components/organisms/RadarChart";
import { SELECTOR_PADDING } from "@/features/player-detail/constants";
import { RadarDataPoint } from "@/utils/radarChartUtils";
import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

/**
 * Outer wrapper width (consumed by a gluestack ui Box).
 * design-system: dynamic-style waiver — data-driven value (measured chart geometry)
 * @param chartSize - Measured chart size in pixels
 * @param selectorPadding - Padding reserved for the metric selectors
 */
const wrapperWidthStyle = (chartSize: number, selectorPadding: number) => ({
  width: chartSize + selectorPadding * 2,
});

/**
 * Inner chart container position/size (consumed by a gluestack ui Box).
 * design-system: dynamic-style waiver — data-driven value (measured chart geometry)
 * @param chartSize - Measured chart size in pixels
 * @param selectorPadding - Padding reserved for the metric selectors
 */
const chartBoxStyle = (chartSize: number, selectorPadding: number) => ({
  top: selectorPadding,
  left: selectorPadding,
  width: chartSize,
  height: chartSize,
});

/**
 * Metric option for the selector
 */
interface MetricOption {
  readonly label: string;
  readonly value: number | null;
}

/**
 * Selected metrics for each axis position
 */
interface SelectedMetrics {
  readonly [position: number]: MetricOption;
}

/**
 * Chart dimensions
 */
interface ChartDimensions {
  readonly width: number;
  readonly height: number;
}

/**
 * Props for RadarChartWithSelectorsView component
 */
interface RadarChartWithSelectorsViewProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Primary radar data series */
  readonly primaryData: readonly RadarDataPoint[];
  /** Secondary radar data series (position average) */
  readonly secondaryData: readonly RadarDataPoint[];
  /** Tertiary radar data series (comparison player) */
  readonly tertiaryData?: readonly RadarDataPoint[];
  /** Selected metrics for each axis position (0-5) */
  readonly selectedMetrics: SelectedMetrics;
  /** All available metric options */
  readonly availableMetrics: readonly MetricOption[];
  /** Handler for metric selection change */
  readonly onMetricChange: (position: number, metric: MetricOption) => void;
  /** Chart size in pixels */
  readonly chartSize: number;
  /** Chart dimensions for RadarChart */
  readonly chartDimensions: ChartDimensions;
  /** Dependent axis style for RadarChart */
  readonly dependentAxisStyle: object;
  /** Independent axis style for RadarChart (with hidden labels) */
  readonly independentAxisStyle: object;
  /** Scatter stroke color */
  readonly scatterStrokeColor: string;
  /** Maximum value for radar domain (supports values > 100) */
  readonly maxValue?: number;
  /** Padding around chart for selector labels (default 80, smaller on mobile) */
  readonly selectorPadding?: number;
  /** Internal Victory chart padding (default 60, smaller on mobile for proportional scaling) */
  readonly chartPadding?: number;
}

/** Player primary color for charts */
const PLAYER_COLOR = "#22d3ee";

/** Comparison player color for charts (orange) */
const COMPARISON_COLOR = "#f97316";

/** Position average color for charts */
const POSITION_AVG_COLOR = "#6b7280";

/** Axis positions for selectors (6 positions matching DEFAULT_LABELS) */
const AXIS_POSITIONS = [0, 1, 2, 3, 4, 5] as const;

/**
 * RadarChartWithSelectorsView renders the radar chart with metric selectors
 * @param props - Component props
 * @param props.isDark - Whether dark mode is active
 * @param props.primaryData - Primary radar data series
 * @param props.secondaryData - Secondary radar data series
 * @param props.tertiaryData - Tertiary radar data series (comparison player)
 * @param props.selectedMetrics - Selected metrics for each position
 * @param props.availableMetrics - All available metric options
 * @param props.onMetricChange - Handler for metric selection change
 * @param props.chartSize - Chart size in pixels
 * @param props.chartDimensions - Chart dimensions for RadarChart
 * @param props.dependentAxisStyle - Dependent axis style
 * @param props.independentAxisStyle - Independent axis style
 * @param props.scatterStrokeColor - Scatter stroke color
 * @param props.maxValue - Maximum value for radar domain
 * @param props.selectorPadding - Padding around chart for selector labels (default 80)
 * @param props.chartPadding - Internal Victory chart padding (default 60, smaller on mobile)
 * @returns Box containing radar chart with positioned selectors
 */
const RadarChartWithSelectorsView = ({
  isDark,
  primaryData,
  secondaryData,
  tertiaryData,
  selectedMetrics,
  availableMetrics,
  onMetricChange,
  chartSize,
  chartDimensions,
  dependentAxisStyle,
  independentAxisStyle,
  scatterStrokeColor,
  maxValue,
  selectorPadding = SELECTOR_PADDING,
  chartPadding = 60,
}: RadarChartWithSelectorsViewProps) => (
  <Box
    {...{
      testID: TEST_ID_PLAYER_DETAIL.RADAR_CHART,
      "data-testid": TEST_ID_PLAYER_DETAIL.RADAR_CHART,
      accessibilityLabel: "Player performance radar chart",
      "aria-label": "Player performance radar chart",
      // role=img / accessibilityRole=image were removed because this
      // wrapper also contains interactive PositionedMetricSelector
      // controls; image role marks a leaf node and hides descendant
      // semantics from assistive tech. The aria-label still names the
      // chart region for screen readers.
    }}
    className="relative mx-auto aspect-square overflow-visible"
    // eslint-disable-next-line no-restricted-syntax -- measured chart wrapper width (chartSize + selectorPadding*2); off the closed sizing ladder, data-driven
    UNSAFE_style={wrapperWidthStyle(chartSize, selectorPadding)}
  >
    {/* Chart container - centered, pointerEvents disabled to not block selectors */}
    <Box
      className="absolute pointer-events-none"
      // eslint-disable-next-line no-restricted-syntax -- measured chart-container position/size (selectorPadding offset, chartSize square); data-driven geometry, no closed token
      UNSAFE_style={chartBoxStyle(chartSize, selectorPadding)}
    >
      <RadarChart
        primarySeries={{
          data: primaryData,
          color: PLAYER_COLOR,
          fillOpacity: 0.3,
          strokeWidth: 2.5,
        }}
        secondarySeries={{
          data: secondaryData,
          color: POSITION_AVG_COLOR,
          fillOpacity: 0.15,
          strokeWidth: 1.5,
          dashed: true,
        }}
        tertiarySeries={
          tertiaryData && tertiaryData.length > 0
            ? {
                data: tertiaryData,
                color: COMPARISON_COLOR,
                fillOpacity: 0.2,
                strokeWidth: 2,
              }
            : undefined
        }
        categoryLabels={AXIS_POSITIONS.map(
          position => selectedMetrics[position]?.label ?? ""
        )}
        dimensions={chartDimensions}
        dependentAxisStyle={dependentAxisStyle}
        independentAxisStyle={independentAxisStyle}
        scatterStrokeColor={scatterStrokeColor}
        padding={chartPadding}
        maxValue={maxValue}
        hideLabels
      />
    </Box>

    {/* Metric selectors positioned around the chart */}
    {AXIS_POSITIONS.map(position => (
      <PositionedMetricSelector
        key={position}
        position={position}
        chartSize={chartSize}
        selectorPadding={selectorPadding}
        metric={selectedMetrics[position]}
        availableMetrics={availableMetrics}
        isDark={isDark}
        onMetricChange={onMetricChange}
      />
    ))}
  </Box>
);

RadarChartWithSelectorsView.displayName = "RadarChartWithSelectorsView";

export default React.memo(RadarChartWithSelectorsView);
